import { OrderColumnEnum, OrderDirectionEnum } from '@koob/enums';
import { getHotels } from 'api/node/hotels';
import { isEqual } from 'lodash';
import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import { decodeId } from 'utils';
import { KoobCard } from '@koob/ui';
import KoobParagraph from '../../../components/Koob/KoobParagraph';
import KoobPlaceholder from '../../../components/Koob/KoobPlaceholder';
import KoobTitle from '../../../components/Koob/KoobTitle';
import { useApp, useSearchParams } from '../../../hooks';
import AllotmentsHotelSelectorItem from './AllotmentsHotelSelectorItem';
import { formatHotelResult } from './allotmentsUtils';
import HotelsActiveFilters from 'components/HotelsActiveFilters';

export default function AllotmentsHotelSelector({
  hotel,
  setHotel,
  kind = 'nightRates',
}) {
  const { t } = useTranslation('allotments');
  // `kind` is a context identifier ('allotments' | 'nightRates') passed by the
  // parent page; it's resolved to a translated label here, falling back to the
  // raw value so an unknown kind still renders something readable.
  const kindLabel = t(`hotelSelector.kinds.${kind}`, { defaultValue: kind });
  const { currentOrganizationId } = useApp();
  const [searchParams] = useSearchParams();
  const { search, kindsIds, searchLocation, state } = searchParams;

  const stars = Boolean(searchParams.stars) ? Number(searchParams.stars) : null;
  const dmcOrganizationId = decodeId(currentOrganizationId);

  const requestFilters = {
    direction: OrderDirectionEnum.DESC,
    order: OrderColumnEnum.UPDATED_AT,
    search,
    stars,
    state,
    ...(searchLocation?.kind && { [searchLocation.kind]: [searchLocation.id] }),
    kinds: Boolean(kindsIds)
      ? (kindsIds ?? []).map(({ value }) => value)
      : undefined,
    dmcId: dmcOrganizationId,
    discarded: false,
    perPage: 100,
    page: 1,
    includeActiveJobs: true,
  };

  const { data: hotelList, loading } = useAsync({
    promiseFn: getHotels,
    filters: requestFilters,
    locale: 'en',
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });
  const hotels = hotelList?.data?.data || [];

  const results =
    hotels
      ?.map((hotel) => formatHotelResult(hotel))
      ?.filter((h) => h.id !== hotel?.id) ?? [];

  return (
    <KoobCard>
      <KoobTitle>{t('hotelSelector.title')}</KoobTitle>
      <KoobParagraph>
        {t('hotelSelector.description', { kind: kindLabel })}
      </KoobParagraph>

      <div className="my-5">
        <HotelsActiveFilters />
      </div>
      <div className="h-[25vh] overflow-scroll bg-gray-50 p-5 rounded-md">
        <div className="grid md:grid-cols-2 gap-3">
          {hotel && (
            <AllotmentsHotelSelectorItem item={hotel} isSelected={true} />
          )}

          {results?.map((item) => (
            <AllotmentsHotelSelectorItem
              key={item.id}
              item={item}
              isSelected={hotel?.id === item.id}
              onSelect={() => setHotel(item)}
            />
          ))}

          {loading && (
            <KoobPlaceholder className="h-24 w-full rounded" count={8} />
          )}
        </div>
      </div>
    </KoobCard>
  );
}
